import { ToggleRightIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import { type ForwardedRef, forwardRef, type PropsWithChildren } from 'react';

import * as styles from './index.css';

export type CategoryDividerProps = PropsWithChildren<
  {
    label: string;
    className?: string;
    collapsed?: boolean;
    setCollapsed?: (collapsed: boolean) => void;
    /**
     * Optional callback when the label text is clicked.
     * If provided, clicking the label triggers this instead of toggling collapse.
     */
    onLabelClick?: () => void;
  } & {
    [key: `data-${string}`]: unknown;
  }
>;

export const CategoryDivider = forwardRef(
  (
    {
      label,
      children,
      className,
      collapsed,
      setCollapsed,
      onLabelClick,
      ...otherProps
    }: CategoryDividerProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const collapsible = collapsed !== undefined;

    const handleClick = () => {
      if (onLabelClick) {
        onLabelClick();
      } else {
        setCollapsed?.(!collapsed);
      }
    };

    const handleCollapseIconClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsed?.(!collapsed);
    };

    return (
      <div
        className={clsx(styles.root, className)}
        ref={ref}
        role="switch"
        onClick={handleClick}
        data-collapsed={collapsed}
        data-collapsible={collapsible}
        {...otherProps}
      >
        <div className={styles.label}>
          {label}
          {collapsible ? (
            <ToggleRightIcon
              width={16}
              height={16}
              data-testid="category-divider-collapse-button"
              className={styles.collapseIcon}
              onClick={handleCollapseIconClick}
            />
          ) : null}
        </div>
        <div className={styles.actions} onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </div>
    );
  }
);

CategoryDivider.displayName = 'CategoryDivider';
